import { webSocketUrl } from "../common/project.config";
import { ElMessage } from "element-plus";
import i18n from "../i18n/index";

const QUIET_MS = 5000;
const RETRY_NOTICE_MS = 15000;
const IDLE_MS = 65000;
const PROBE_MS = 8000;
const AUTH_MS = 12000;
const socketTimers = ["wsHeartbeatTimer", "wsAuthTimer", "wsProbeTimer", "wsSyncTimer"];

function online() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}
function visible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}
function eligible(state) {
  return state.User.isLogin && state.User.status == 1 &&
    /^[a-f0-9]{32}$/.test(localStorage.getItem("token") || "");
}
function clearTimer(state, name) {
  clearTimeout(state[name]);
  state[name] = null;
}
function diagnostic(state, event, details = {}) {
  const entry = { at: Date.now(), event, idleMs: Math.max(0, Date.now() - state.wsLastReceived),
    attempt: state.wsReconnectAttempt, ...details };
  state.wsDiagnostics.push(entry);
  if (state.wsDiagnostics.length > 20) state.wsDiagnostics.shift();
  // Only connection timing and numeric close codes; never tokens, URLs or message bodies.
  if (typeof console !== "undefined" && console.info) console.info("[chat-connection]", entry);
}
function clearProblem(state) {
  clearTimer(state, "wsNoticeTimer");
  clearTimer(state, "wsRetryNoticeTimer");
  state.wsProblemSince = null;
  state.wsNoticeKey = "";
  state.wsShowRetry = false;
}
function problem(state, status) {
  state.wsStatus = status;
  if (state.wsProblemSince === null) state.wsProblemSince = Date.now();
  const since = state.wsProblemSince;
  const update = () => {
    if (state.wsProblemSince !== since) return;
    const elapsed = Date.now() - since;
    state.wsNoticeKey = state.wsStatus === "offline" ? "chat.offline" :
      elapsed >= QUIET_MS ? (state.wsWasConnected ? "chat.reconnecting" : "chat.connecting") : "";
    state.wsShowRetry = state.wsStatus !== "offline" && elapsed >= RETRY_NOTICE_MS;
  };
  clearTimer(state, "wsNoticeTimer");
  clearTimer(state, "wsRetryNoticeTimer");
  update();
  const elapsed = Date.now() - since;
  if (elapsed < QUIET_MS) state.wsNoticeTimer = setTimeout(update, QUIET_MS - elapsed);
  if (elapsed < RETRY_NOTICE_MS) state.wsRetryNoticeTimer = setTimeout(update, RETRY_NOTICE_MS - elapsed);
}
function dropSocket(state) {
  socketTimers.forEach(name => clearTimer(state, name));
  state.wsProbe = null;
  state.wsSyncPending = false;
  const ws = state.ws;
  state.ws = null;
  state.wsReady = false;
  if (ws) {
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    ws.close();
  }
}
function authTimeout(state, dispatch, ws) {
  clearTimer(state, "wsAuthTimer");
  if (!visible()) return;
  state.wsAuthTimer = setTimeout(() => {
    if (state.ws !== ws) return;
    state.wsAuthTimer = null;
    if (!state.wsReady && visible()) dispatch("breakWebsocket", "auth-timeout");
  }, AUTH_MS);
}

export default {
  state: {
    ws: null, wsReady: false, wsStatus: "disconnected", wsWasConnected: false,
    wsReconnectTimer: null, wsHeartbeatTimer: null, wsAuthTimer: null,
    wsProbeTimer: null, wsSyncTimer: null, wsNoticeTimer: null, wsRetryNoticeTimer: null,
    wsLastReceived: 0, wsProblemSince: null, wsNoticeKey: "", wsShowRetry: false,
    wsReconnectAttempt: 0, wsProbeSequence: 0, wsProbe: null, wsSyncPending: false,
    wsResumeListener: null, wsDiagnostics: [],
  },
  mutations: {
    closeWebsocket(state) {
      clearTimer(state, "wsReconnectTimer");
      dropSocket(state);
      clearProblem(state);
      state.wsStatus = "disconnected";
      state.wsWasConnected = false;
      state.wsReconnectAttempt = 0;
      state.wsDiagnostics = [];
      if (state.wsResumeListener && typeof window !== "undefined") {
        ["online", "offline", "pageshow"].forEach(event => window.removeEventListener(event, state.wsResumeListener));
        document.removeEventListener("visibilitychange", state.wsResumeListener);
        state.wsResumeListener = null;
      }
    },
  },
  actions: {
    listenWebsocket({ state, dispatch, commit }) {
      if (state.wsResumeListener || typeof window === "undefined") return;
      state.wsResumeListener = event => {
        if (event.type === "offline") return dispatch("offlineWebsocket");
        if (!visible()) {
          commit("checkpointChatHistory");
          clearTimer(state, "wsHeartbeatTimer");
          clearTimer(state, "wsAuthTimer");
          clearTimer(state, "wsProbeTimer");
          return;
        }
        dispatch("recoverWebsocket", { resume: true });
      };
      ["online", "offline", "pageshow"].forEach(event => window.addEventListener(event, state.wsResumeListener));
      document.addEventListener("visibilitychange", state.wsResumeListener);
    },
    initWebsocket({ dispatch, commit, state }) {
      if (!eligible(state) || state.ws || state.wsReconnectTimer !== null) return;
      dispatch("listenWebsocket");
      if (!online()) return dispatch("offlineWebsocket");
      const token = localStorage.getItem("token");
      problem(state, state.wsWasConnected ? "reconnecting" : "connecting");
      let ws;
      try { ws = new WebSocket(webSocketUrl); }
      catch (error) {
        diagnostic(state, "connect-error");
        return dispatch("scheduleWebsocketReconnect");
      }
      state.ws = ws;
      state.wsReady = false;
      state.wsLastReceived = Date.now();
      authTimeout(state, dispatch, ws);
      ws.onopen = () => {
        if (state.ws !== ws) return;
        try { ws.send(JSON.stringify({ opt: "100", hasInit: "no", token, origin: true })); }
        catch (error) { return dispatch("breakWebsocket", "init-send-error"); }
        dispatch("watchWebsocket");
      };
      ws.onmessage = response => {
        if (state.ws !== ws) return;
        let data;
        try { data = JSON.parse(response.data); } catch (error) { return; }
        if (!data || typeof data !== "object" || Array.isArray(data)) return;
        if (data.type === "heartbeat_ack") {
          if (!state.wsProbe || data.request_id !== state.wsProbe.id) return;
        } else if (data.type !== "ping" &&
            !([0, 1, 3, 4, 5, 6, 10, 20].includes(Number(data.opt)) && ["success", "error"].includes(data.status))) return;
        state.wsLastReceived = Date.now();
        if (Number(data.opt) === 10) {
          commit("logout");
          commit("closeWebsocket");
          commit("clearChatImages");
          commit("clearChatHistory");
          commit("clearChatSession");
          return;
        }
        dispatch("acceptWebsocketTraffic", { snapshotReceived: Number(data.opt) === 0 && data.status === "success" });
        if (data.type === "ping") {
          try { ws.send(JSON.stringify({ type: "pong" })); }
          catch (error) { dispatch("breakWebsocket", "pong-send-error"); }
          return;
        }
        if (data.type === "heartbeat_ack") return;
        data.opt = Number(data.opt);
        if (data.status === "success") {
          if (data.opt === 0) {
            if (state.wsProblemSince !== null) diagnostic(state, "connected", { recoveryMs: Date.now() - state.wsProblemSince });
            clearTimer(state, "wsAuthTimer");
            clearTimer(state, "wsSyncTimer");
            state.wsSyncPending = false;
            state.wsReady = true;
            state.wsWasConnected = true;
            state.wsReconnectAttempt = 0;
            state.wsStatus = "connected";
            clearProblem(state);
          }
          dispatch("chatEvent", data);
          if (data.opt === 0) dispatch("resumeChatImages");
        } else if (data.opt === 1) {
          if (data.client_msg_id) commit("failChatImage", data);
          else ElMessage.error(i18n.global.t(data.error_key || "chat.sendFailed"));
        }
      };
      ws.onclose = event => {
        if (state.ws !== ws) return;
        diagnostic(state, "closed", { code: event && event.code, wasClean: !!(event && event.wasClean) });
        dropSocket(state);
        dispatch("pauseChatImages");
        commit("checkpointChatHistory");
        commit("clearChatHistory");
        if (eligible(state) && localStorage.getItem("token") === token) dispatch("scheduleWebsocketReconnect");
      };
      ws.onerror = () => { if (state.ws === ws) dispatch("breakWebsocket", "socket-error"); };
    },
    scheduleWebsocketReconnect({ state, dispatch }) {
      if (!eligible(state) || state.ws || state.wsReconnectTimer !== null) return;
      if (!online()) return dispatch("offlineWebsocket");
      problem(state, state.wsWasConnected ? "reconnecting" : "connecting");
      const attempt = state.wsReconnectAttempt++;
      const base = attempt === 0 ? 0 : Math.min(15000, 1000 * Math.pow(2, Math.min(attempt - 1, 4)));
      const delay = Math.min(15000, Math.round(base * (0.8 + Math.random() * 0.4)));
      diagnostic(state, "retry-scheduled", { delayMs: delay });
      state.wsReconnectTimer = setTimeout(() => {
        state.wsReconnectTimer = null;
        dispatch("initWebsocket");
      }, delay);
    },
    breakWebsocket({ state, dispatch, commit }, reason) {
      if (!state.ws) return;
      diagnostic(state, reason);
      dropSocket(state);
      dispatch("pauseChatImages");
      commit("checkpointChatHistory");
      commit("clearChatHistory");
      dispatch("scheduleWebsocketReconnect");
    },
    offlineWebsocket({ state, dispatch, commit }) {
      if (!eligible(state)) return;
      clearTimer(state, "wsReconnectTimer");
      dropSocket(state);
      dispatch("pauseChatImages");
      commit("checkpointChatHistory");
      commit("clearChatHistory");
      problem(state, "offline");
      diagnostic(state, "offline");
    },
    watchWebsocket({ state, dispatch }) {
      clearTimer(state, "wsHeartbeatTimer");
      const ws = state.ws;
      if (!ws || !visible()) return;
      state.wsHeartbeatTimer = setTimeout(() => {
        if (state.ws !== ws) return;
        state.wsHeartbeatTimer = null;
        if (!visible()) return;
        if (!online()) return dispatch("offlineWebsocket");
        if (state.wsReady && Date.now() - state.wsLastReceived > IDLE_MS) dispatch("probeWebsocket", { blocking: true });
        dispatch("watchWebsocket");
      }, 10000);
    },
    probeWebsocket({ state, dispatch }, { blocking = false, resume = false } = {}) {
      const ws = state.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN || !visible()) return;
      if (state.wsProbe) {
        state.wsProbe.resume = state.wsProbe.resume || resume;
        // A probe paused in the background gets a fresh confirmation window on resume.
        if (state.wsProbeTimer !== null) return;
      } else {
        state.wsProbe = { id: String(++state.wsProbeSequence), at: Date.now(), wasReady: state.wsReady, resume };
        if (blocking) {
          state.wsReady = false;
          problem(state, "checking");
        }
      }
      const probe = state.wsProbe;
      probe.at = Date.now();
      diagnostic(state, "probe-start");
      try { ws.send(JSON.stringify({ type: "heartbeat_probe", request_id: probe.id })); }
      catch (error) { return dispatch("breakWebsocket", "probe-send-error"); }
      state.wsProbeTimer = setTimeout(() => {
        if (state.ws !== ws || state.wsProbe !== probe) return;
        state.wsProbeTimer = null;
        if (visible()) dispatch("breakWebsocket", "probe-timeout");
      }, PROBE_MS);
    },
    acceptWebsocketTraffic({ state, dispatch }, { snapshotReceived = false } = {}) {
      const probe = state.wsProbe;
      if (!probe) return;
      clearTimer(state, "wsProbeTimer");
      state.wsProbe = null;
      diagnostic(state, "probe-confirmed", { probeMs: Date.now() - probe.at });
      if (probe.wasReady) {
        state.wsReady = true;
        state.wsStatus = "connected";
        clearProblem(state);
        dispatch("resumeChatImages");
        if (probe.resume && !snapshotReceived) dispatch("refreshWebsocketSnapshot");
      }
    },
    refreshWebsocketSnapshot({ state, dispatch, commit }) {
      const ws = state.ws;
      if (!state.wsReady || !ws || state.wsSyncPending) return;
      // Refresh unread counts and fill history gaps without disabling an authenticated connection.
      state.wsSyncPending = true;
      commit("checkpointChatHistory");
      try { ws.send(JSON.stringify({ opt: "100", hasInit: "no", token: localStorage.getItem("token"), origin: true })); }
      catch (error) { return dispatch("breakWebsocket", "snapshot-send-error"); }
      state.wsSyncTimer = setTimeout(() => {
        if (state.ws !== ws) return;
        state.wsSyncTimer = null;
        if (!state.wsSyncPending) return;
        state.wsSyncPending = false;
        diagnostic(state, "snapshot-timeout");
      }, AUTH_MS);
    },
    recoverWebsocket({ state, dispatch, commit }, { resume = false } = {}) {
      if (!eligible(state)) return;
      if (!online()) return dispatch("offlineWebsocket");
      const ws = state.ws;
      // Capture the cursor before a live push can overtake the recovery snapshot.
      if (resume) commit("checkpointChatHistory");
      if (ws && ws.readyState === 0) {
        if (state.wsAuthTimer === null) authTimeout(state, dispatch, ws);
        return;
      }
      if (ws && ws.readyState === WebSocket.OPEN) {
        dispatch("watchWebsocket");
        if (state.wsProbe || state.wsReady) {
          return dispatch("probeWebsocket", { blocking: Date.now() - state.wsLastReceived > IDLE_MS, resume });
        }
        if (state.wsAuthTimer === null) authTimeout(state, dispatch, ws);
        return;
      }
      if (ws) return dispatch("breakWebsocket", "unavailable-on-resume");
      clearTimer(state, "wsReconnectTimer");
      dispatch("initWebsocket");
    },
    wssend({ state, dispatch }, data) {
      if (state.wsReady && state.ws && state.ws.readyState === WebSocket.OPEN) {
        try { state.ws.send(JSON.stringify(data)); return true; }
        catch (error) { dispatch("breakWebsocket", "message-send-error"); }
      }
      if (data.opt === 1 && !data.client_msg_id) ElMessage.error(i18n.global.t("chat.connectionLost"));
      // Sending during recovery must not bypass backoff or create duplicate sockets.
      if (!state.ws && state.wsReconnectTimer === null) dispatch("initWebsocket");
      return false;
    },
  },
};
